/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {OnAlertsUpdated} from "@modules/AlertsModule";
import {arrayToMap, filterInstances} from "@intuitionrobotics/ts-common";
import {DB_Patient} from "@app/app-shared/patient";
import {capitalize, DB_User, getUserNameWithTitle} from "@app/ir-q-app-common/types/care-center/user";
import {UsersModule} from "@modules/UsersModule";
import {hideTooltip} from "../widget/appTooltip";
import {Loader} from "../../../widgets/Loader";
import {OnWindowResize} from "../../App";
import {ClearFilter} from "../../utils/ClearFilter";
import {SelectOrganization} from "../widget/SelectOrganization";
import {PatientsTable} from "../ui/patients/PatientsTable";
import {PatientContextProvider} from "../ui/patients/PatientsContext";
import {MultiSelectDropDown, MultiSelectOption} from "../../../widgets/MultiSelectDropdown";
import {LastUpdated, PatientsDataModule} from "@modules/PatientDataModule";
import {ID_ALL} from "@app/app-shared/permission";

export type TableRow = {
    patientName: DB_Patient
    unitId: { unitId: string, id: string }
    physician: { physician?: DB_User, id: string }
    lastInteraction: { lastInteraction: string, timestamp: number, id: string }
    careManager: { careManager?: DB_User, id: string }
}

type State = {
    physicianFilter: Set<string>
    careManagerFilter: Set<string>
    customersFilter: Set<string>
    orgFilter: Set<string>
    // newFirst will only be used when the page loads.
    // Using newFirst will display all the new patients first, and then sort the others alphabetically.
    sort: { filterColumn: keyof TableRow | "newFirst", invert: boolean }
}

export const tableCardStyle: CSSProperties = {
    backgroundColor: "white",
    boxShadow: "0 0 3px 1px #ececec",
    borderRadius: "6px",
    padding: 16,
    margin: 16,
    marginBottom: 0,
    marginTop: 0
};

export class PatientsHome
    extends BaseComponent<{}, State>
    implements OnAlertsUpdated, OnWindowResize, LastUpdated {

    __onWindowResize = () => this.forceUpdate();
    __onAlertsUpdated = () => this.forceUpdate();
    __lastUpdated = () => this.forceUpdate();

    constructor(props: {}) {
        super(props);
        this.state = {
            sort: {filterColumn: "newFirst", invert: true},
            physicianFilter: new Set(),
            careManagerFilter: new Set(),
            customersFilter: new Set(),
            orgFilter: new Set()
        };
    }

    componentWillUnmount() {
        hideTooltip();
    }


    renderFilterDropdown = (options: MultiSelectOption[], stateKey: "physicianFilter" | "careManagerFilter" | "customersFilter") => {
        return <div className={"ll_h_c"}>
            <MultiSelectDropDown
                labels={{main: capitalize(stateKey.replace("Filter", "").replace(/(?<!^)([A-Z])/g, ' $1'))}}
                options={options}
                onItemClick={(value, checked) => {
                    if (value === ID_ALL)
                        return this.setState(s => ({...s, [stateKey]: new Set()}));

                    this.setState(s => {
                        const set = s[stateKey];
                        if (checked)
                            set.add(value);
                        else
                            set.delete(value);
                        return {...s, [stateKey]: set};
                    })
                }}
            />
        </div>;
    };

    private renderHead = (patients: DB_Patient[]) => {
        const users = UsersModule.getUsers();
        const userMap = arrayToMap(users, user => user._id);

        const patientsDropdownOptions = this.getPatientsDropdownOptions(patients);
        const physicianDropdownOptions = this.getUserRoleFilter(patients, userMap, "physicianId", "physicianFilter");
        const careManagerDropdownOptions = this.getUserRoleFilter(patients, userMap, "careManagerId", "careManagerFilter");

        return <div style={{display: "flex", flex: 1}}>
            <SelectOrganization
                onOrgClicked={(orgId) => {
                    if (orgId === ID_ALL)
                        return this.setState(s => ({...s, orgFilter: new Set()}));
                    this.setState(s => {
                        const orgs = s.orgFilter;
                        if (orgs.has(orgId))
                            orgs.delete(orgId);
                        else
                            orgs.add(orgId);
                        return {...s, orgFilter: orgs};
                    });
                }}
                selected={this.state.orgFilter}
            />
            {this.renderFilterDropdown(patientsDropdownOptions, "customersFilter")}
            {this.renderFilterDropdown(physicianDropdownOptions, "physicianFilter")}
            {this.renderFilterDropdown(careManagerDropdownOptions, "careManagerFilter")}
            {this.state.customersFilter.size !== 0 ||
                this.state.careManagerFilter.size !== 0 ||
                this.state.physicianFilter.size !== 0 &&
                <ClearFilter onClick={() => {
                    this.setState({
                        customersFilter: new Set(),
                        physicianFilter: new Set(),
                        careManagerFilter: new Set(),
                        orgFilter: new Set(),
                        sort: {filterColumn: "patientName", invert: false}
                    });
                }}/>}
        </div>;
    };


    private getPatientsDropdownOptions(patients: DB_Patient[]) {
        return [{
            label: "All",
            value: ID_ALL,
            checked: this.state.customersFilter.size === 0
        }, ...patients
            .sort((u1, u2) => u1.name.localeCompare(u2.name))
            .map((p) => ({
                label: p.name,
                value: p._id,
                checked: this.state.customersFilter.has(p._id)
            }))];
    }

    private getUserRoleFilter(
        patients: DB_Patient[],
        userMap: { [p: string]: DB_User },
        k: "physicianId" | "careManagerId",
        stateFilterKey: "physicianFilter" | "careManagerFilter"
    ): MultiSelectOption[] {
        const counterMap = new Map<DB_User, number>();
        patients.forEach(p => {
            const patientEl = p[k];
            if (!patientEl)
                return;
            const user = userMap[patientEl];
            if (user)
                counterMap.set(user, (counterMap.get(user) || 0) + 1);
        });

        return [{
            label: "All",
            value: ID_ALL,
            checked: this.state[stateFilterKey].size === 0
        }, ...[...counterMap.entries()]
            .sort(([u1], [u2]) => getUserNameWithTitle(u1).localeCompare(getUserNameWithTitle(u2)))
            .map(([user, count]) => ({
                label: getUserNameWithTitle(user),
                value: user._id,
                checked: this.state[stateFilterKey].has(user._id),
                count
            }))];
    }

    isOptionFiltered = (patient: DB_Patient, physician?: DB_User, careManager?: DB_User) => {
        const filterOrg = this.state.orgFilter.size === 0 || this.state.orgFilter.has(patient.organizationId);
        const filterPhysician = this.state.physicianFilter.size === 0 || (physician && this.state.physicianFilter.has(physician._id));
        const filterCareManager = this.state.careManagerFilter.size === 0 || (careManager && this.state.careManagerFilter.has(careManager._id));
        const filterPatients = this.state.customersFilter.size === 0 || this.state.customersFilter.has(patient._id);
        return filterPhysician && filterCareManager && filterPatients && filterOrg;
    };

    private getContent = (patients: DB_Patient[], demo: boolean): TableRow[] => {
        const users = UsersModule.getUsers();

        const userMap = arrayToMap(users, user => user._id);
        return patients.reduce((acc: TableRow[], _patient: DB_Patient) => {

            const careManager = _patient.careManagerId ? userMap[_patient.careManagerId] : undefined;
            const physician = _patient.physicianId ? userMap[_patient.physicianId] : undefined;

            if (!this.isOptionFiltered(_patient, physician, careManager))
                return acc;

            const {formattedDateTime, timestamp} = PatientsDataModule.getLastUserActionFormatted(_patient.unitId, demo)
            const row = {
                patientName: _patient,
                unitId: {unitId: _patient.unitId, id: _patient._id},
                careManager: {careManager, id: _patient._id},
                physician: {physician, id: _patient._id},
                lastInteraction: {lastInteraction: formattedDateTime, timestamp, id: _patient._id}
            };
            return [...acc, row];
        }, []);
    };

    render() {
        return <PatientContextProvider roles={[]}>
            {({patientIds, patients: pMap, demo}) => {
                const patients = filterInstances(patientIds.map(_id => pMap[_id]));
                return <div className={"fill page_table_wrapper"}>
                    {!patientIds.length && <Loader/>}
                    {this.renderHead(patients)}
                    <PatientsTable content={this.getContent(patients, demo)}/>
                </div>
            }}
        </PatientContextProvider>
    }
}
